import './RoasCompositionTable.scss';
import { useMemo, FC, JSX } from 'react';
import { BPMTable, TypedHeader } from '@Components/StickyTable/BPMTable';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { abbreviateNumber, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { CompositionRow } from '../hroas-types';
import { formatPercentRounded } from '../utils/hroasUtils';

const ROAS_DECIMAL_PLACES = 2;

const NoRowsMessage = () => <div className='rc-table-empty'>No data to display.</div>;

interface RoasCompositionTableProps {
  sortedRows: CompositionRow[];
  compositionDataRevenue: CompositionRow[];
  compositionDataRoas: CompositionRow[];
  enabledModels: {
    id: string;
    label: string;
    appLabel: string;
    enabled: boolean;
    weight: number;
  }[];
  visibleModelIds: string[];
  headerOutcome: 'ROAS' | 'Revenue';
  actualVsRelative: 'Actual' | 'Relative';
  groupBy: string;
  onRowClick: (label: string) => void;
}

const formatModelValue = (
  value: number,
  rowSum: number,
  isRevenueColumns: boolean,
  isRelative: boolean
): string => {
  if (isRelative) {
    return rowSum > 0 ? formatPercentRounded((value / rowSum) * 100) : '0%';
  }
  return isRevenueColumns
    ? `$${abbreviateNumber(value, 1)}`
    : formatNumber(value, ROAS_DECIMAL_PLACES);
};

const formatTotalValue = (
  row: CompositionRow,
  visibleModelIds: string[],
  isRevenueColumns: boolean,
  isRelative: boolean
): string => {
  if (isRelative) {
    return '100%';
  }
  if (isRevenueColumns) {
    const totalRevenue = visibleModelIds.reduce((s, id) => s + (row.valuesByModel[id] ?? 0), 0);
    return `$${abbreviateNumber(totalRevenue, 1)}`;
  }
  const totalRoas = visibleModelIds.reduce((s, id) => s + (row.valuesByModel[id] ?? 0), 0);
  return formatNumber(totalRoas, ROAS_DECIMAL_PLACES);
};

export const RoasCompositionTable: FC<RoasCompositionTableProps> = ({
  sortedRows,
  compositionDataRevenue,
  compositionDataRoas,
  enabledModels,
  visibleModelIds,
  headerOutcome,
  actualVsRelative,
  groupBy,
  onRowClick
}) => {
  const isRevenueColumns = headerOutcome === 'Revenue';
  const isRelative = actualVsRelative === 'Relative';

  const activeData = isRevenueColumns ? compositionDataRevenue : compositionDataRoas;

  const allTableRows = useMemo(() => {
    const activeByLabel: Record<string, CompositionRow> = {};
    activeData.forEach(r => {
      activeByLabel[r.label] = r;
    });
    return sortedRows.map(row => activeByLabel[row.label] ?? row);
  }, [sortedRows, activeData]);

  const channelTableRows = useMemo(
    () => allTableRows.filter(r => r.label !== 'Overall'),
    [allTableRows]
  );

  const overallRow = useMemo(() => allTableRows.find(r => r.label === 'Overall'), [allTableRows]);

  const headers = useMemo((): TypedHeader<CompositionRow>[] => {
    const labelCol: TypedHeader<CompositionRow> = {
      label: groupBy,
      name: 'label',
      flex: 2,
      minFlexWidth: 140,
      nonFilterable: true,
      contentGetter: (row: CompositionRow) => row.label,
      renderer: (row: CompositionRow) => (
        <InteractiveDiv
          className='rc-table-cell rc-table-label-cell'
          onClick={() => onRowClick(row.label)}
        >
          {row.label}
        </InteractiveDiv>
      )
    };

    const spendCol: TypedHeader<CompositionRow> = {
      label: 'Spend',
      name: 'spend',
      flex: 1,
      minFlexWidth: 80,
      nonFilterable: true,
      contentGetter: (row: CompositionRow) =>
        Math.max(...visibleModelIds.map(id => row.rowSpendByModel?.[id] ?? 0)),
      renderer: (row: CompositionRow) => {
        const spend = Math.max(...visibleModelIds.map(id => row.rowSpendByModel?.[id] ?? 0));
        return (
          <InteractiveDiv
            className='rc-table-cell rc-table-numeric-cell'
            onClick={() => onRowClick(row.label)}
          >
            {`$${abbreviateNumber(spend, 1)}`}
          </InteractiveDiv>
        );
      }
    };

    const modelCols: TypedHeader<CompositionRow>[] = visibleModelIds.map(modelId => {
      const modelLabel = enabledModels.find(m => m.id === modelId)?.appLabel ?? modelId;
      return {
        label: `${modelLabel} ${isRevenueColumns ? 'Revenue' : 'ROAS'}`,
        name: modelId,
        flex: 1,
        minFlexWidth: 80,
        nonFilterable: true,
        contentGetter: (row: CompositionRow) => row.valuesByModel[modelId] ?? 0,
        renderer: (row: CompositionRow) => {
          const value = row.valuesByModel[modelId] ?? 0;
          const rowSum = visibleModelIds.reduce((s, id) => s + (row.valuesByModel[id] ?? 0), 0);
          return (
            <InteractiveDiv
              className='rc-table-cell rc-table-numeric-cell'
              onClick={() => onRowClick(row.label)}
            >
              {formatModelValue(value, rowSum, isRevenueColumns, isRelative)}
            </InteractiveDiv>
          );
        }
      };
    });

    const totalCol: TypedHeader<CompositionRow> = {
      label: `Total ${isRevenueColumns ? 'Revenue' : 'ROAS'}`,
      name: 'total',
      flex: 1,
      minFlexWidth: 80,
      nonFilterable: true,
      contentGetter: (row: CompositionRow) =>
        visibleModelIds.reduce((s, id) => s + (row.valuesByModel[id] ?? 0), 0),
      renderer: (row: CompositionRow) => (
        <InteractiveDiv
          className='rc-table-cell rc-table-numeric-cell rc-table-total-cell'
          onClick={() => onRowClick(row.label)}
        >
          {formatTotalValue(row, visibleModelIds, isRevenueColumns, isRelative)}
        </InteractiveDiv>
      )
    };

    return [labelCol, spendCol, ...modelCols, totalCol];
  }, [groupBy, isRevenueColumns, isRelative, visibleModelIds, enabledModels, onRowClick]);

  const overallTotals = useMemo((): Partial<CompositionRow> | undefined => {
    if (!overallRow) {
      return undefined;
    }
    const spend = Math.max(...visibleModelIds.map(id => overallRow.rowSpendByModel?.[id] ?? 0));
    const rowSum = visibleModelIds.reduce((s, id) => s + (overallRow.valuesByModel[id] ?? 0), 0);
    const result: Record<string, JSX.Element> = {
      label: (
        <InteractiveDiv
          className='rc-table-cell rc-table-label-cell'
          onClick={() => onRowClick('Overall')}
        >
          Overall
        </InteractiveDiv>
      ),
      spend: (
        <InteractiveDiv
          className='rc-table-cell rc-table-numeric-cell'
          onClick={() => onRowClick('Overall')}
        >
          {`$${abbreviateNumber(spend, 1)}`}
        </InteractiveDiv>
      ),
      total: (
        <InteractiveDiv
          className='rc-table-cell rc-table-numeric-cell rc-table-total-cell'
          onClick={() => onRowClick('Overall')}
        >
          {formatTotalValue(overallRow, visibleModelIds, isRevenueColumns, isRelative)}
        </InteractiveDiv>
      )
    };
    visibleModelIds.forEach(modelId => {
      const value = overallRow.valuesByModel[modelId] ?? 0;
      result[modelId] = (
        <InteractiveDiv
          className='rc-table-cell rc-table-numeric-cell'
          onClick={() => onRowClick('Overall')}
        >
          {formatModelValue(value, rowSum, isRevenueColumns, isRelative)}
        </InteractiveDiv>
      );
    });
    return result;
  }, [overallRow, visibleModelIds, isRevenueColumns, isRelative, onRowClick]);

  return (
    <div className='rc-table-container'>
      <BPMTable
        data={channelTableRows}
        headers={headers}
        totals={overallTotals}
        filterBar={false}
        alternateColors={false}
        noRowsRenderer={NoRowsMessage}
      />
    </div>
  );
};
